import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { Subject, filter } from 'rxjs';
import { CustomBadgeRendererComponent } from 'src/app/components/custom-badge-renderer/custom-badge-renderer.component';
import { ActionButtonsComponent } from 'src/app/modules/rate-manager/components/action-buttons/action-buttons.component';
import { PopoverInfoComponent } from 'src/app/modules/rate-manager/components/popover-info/popover-info.component';

@Injectable({
  providedIn: 'root',
})
export class RateListingsService {
  private refreshGridSubject = new Subject<void>();

  refreshGrid$ = this.refreshGridSubject.asObservable();

  constructor() {}

  triggerGridRefresh() {
    this.refreshGridSubject.next();
  }

  getColumnDefs = (data: any[]): ColDef[] => {
    const unwantedColumns = ['id', 'provider_id', 'is_active'];

    const getWidthFromHeader = (header: string) => {
      const charWidth = 10; // Pixels per character (average)
      const padding = 60; // Extra buffer for sorting arrows, filtering icons, and cell spacing
      return header.length * charWidth + padding;
    };

    // Default columns to show even if data is empty
    const defaultDefs: ColDef[] = [
      {
        field: 'action',
        headerName: '',
        width: 55,
        minWidth: 55,
        maxWidth: 55,
        flex: 0,
        filter: false,
        floatingFilter: false,
        pinned: 'left',
        lockPosition: 'left',
        lockPinned: true,
        lockVisible: true,
        cellRenderer: ActionButtonsComponent,
        cellRendererParams: { onStatusUpdate: () => this.triggerGridRefresh() },
      },
      {
        field: 'status',
        headerName: 'Status',
        editable: false,
        filter: 'agTextColumnFilter',
        minWidth: getWidthFromHeader('Status'),
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ['Draft', 'In Review', 'Live', 'Deactivated'] },
        cellRenderer: CustomBadgeRendererComponent,
        cellRendererParams: { componentName: 'provider_rate_listing' },
        headerComponent: PopoverInfoComponent,
        headerComponentParams: {
          title: 'Status',
          contentHtml: `<div class="d-flex flex-column gap-2">
            <div><strong>In Review</strong> – We are reviewing the rates and it may be published soon</div>
            <div><strong>Live</strong> – Customer can see your rates and quotes being generated</div>
          </div>`,
          iconClass: 'bi-info-circle-fill text-info',
          placement: 'top',
          trigger: 'hover',
        },
      },
      {
        field: 'origin_name',
        headerName: 'Origin',
        filter: 'agTextColumnFilter',
        minWidth: getWidthFromHeader('Origin'),
      },
      {
        field: 'destination_name',
        headerName: 'Destination',
        filter: 'agTextColumnFilter',
        minWidth: getWidthFromHeader('Destination'),
      }
    ];

    if (!Array.isArray(data) || data.length === 0) return defaultDefs;


    // 1️⃣ Collect all keys for dynamic columns (including rate types and bracket ranges)
    const rateKeys = new Set<string>();
    const bracketKeys = new Set<string>();

    data.forEach((item) => {
      // rates keys
      if (item.rates && typeof item.rates === 'object') {
        Object.keys(item.rates).forEach((key) => rateKeys.add(key));
      }

      // rate_brackets keys
      if (Array.isArray(item.rate_brackets)) {
        item.rate_brackets.forEach((bracket: any) => {
          const fieldName = this.formatBracketFieldName(
            bracket.range_from,
            bracket.range_to,
            bracket.unit_code
          );
          const headerName = this.formatBracketHeaderName(
            bracket.range_from,
            bracket.range_to,
            bracket.unit_code
          );
          bracketKeys.add(
            JSON.stringify({ field: fieldName, header: headerName })
          );
        });
      }
    });

    // 2️⃣ Take the first row as reference for standard columns
    const sample = data[0];
    let columnDefs: ColDef[] = Object.keys(sample)
      .filter(
        (key) =>
          !unwantedColumns.includes(key) &&
          key !== 'rates' &&
          key !== 'rate_brackets'
      )
      .map((key): ColDef => {
        const headerName = this.formatText(key);

        if (key === 'zone_from' || key === 'zone_to') {
          return {
            field: key,
            headerName,
            filter: 'agTextColumnFilter',
            minWidth: getWidthFromHeader(headerName || ''),
            headerComponent: PopoverInfoComponent,
            headerComponentParams: {
              title: headerName,
              contentHtml: `<div class="d-flex flex-column">
              <span class="text-sm">To add or edit zones, go to <a href="#">Location settings</a></span>
            </div>`,
              iconClass: 'bi-info-circle-fill text-info',
              popoverClass: '',
              placement: 'top',
              trigger: 'manual',
            },
          };
        }

        if (key === 'status') {
          return {
            field: key,
            headerName: 'Status',
            editable: false,
            minWidth: getWidthFromHeader('Status'),
            sortable: false,
            filter: false,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['Draft', 'In Review', 'Live', 'Deactivated'] },
            cellRenderer: CustomBadgeRendererComponent,
            cellRendererParams: { componentName: 'provider_rate_listing' },
            headerComponent: PopoverInfoComponent,
            headerComponentParams: {
              title: 'Status',
              contentHtml: `<div class="d-flex flex-column gap-2">
                <div><strong>In Review</strong> – We are reviewing the rates and it may be published soon</div>
                <div><strong>Live</strong> – Customer can see your rates and quotes being generated</div>
              </div>`,
              iconClass: 'bi-info-circle-fill text-info',
              placement: 'top',
              trigger: 'hover',
            },
          };
        }

         if (key === 'origin') {
          return {
            field: 'origin_name',
            headerName: 'Origin',
            filter: 'agTextColumnFilter',
            minWidth: getWidthFromHeader('Origin'),
          }
         }

         if (key === 'destination') {
          return {
            field: 'destination_name',
            headerName: 'Destination',
            filter: 'agTextColumnFilter',
            minWidth: getWidthFromHeader('Destination'),
          }
         }

         if (key === 'rate_unit') {
           return {
             field: key,
             headerName: headerName,
             filter: 'agSetColumnFilter',
             sortable: false,
             filterParams: {
               values: ['per_unit', 'per_kg'],
               defaultToNothingSelected: false,
             },
             minWidth: getWidthFromHeader(headerName || ''),
           };
         }

         if (key === 'pickup_type' || key === 'delivery_type') {
           return {
             field: key,
             headerName: headerName,
             filter: 'agSetColumnFilter',
             sortable: false,
             filterParams: {
               values: ['door', 'depot'],
               defaultToNothingSelected: false,
             },
             minWidth: getWidthFromHeader(headerName || ''),
           };
         }

        if (key === 'route_rate_ids') {
          return {
            field: key,
            headerName,
            filter: 'agTextColumnFilter',
            sortable: false,
            minWidth: getWidthFromHeader(headerName || ''),
          };
        }

        if (key === 'rate_category') {
          return {
            field: key,
            headerName,
            sortable: false,
            filter: false,
            minWidth: getWidthFromHeader(headerName || ''),
          };
        }

        return {
          field: key,
          headerName,
          filter: 'agTextColumnFilter',
          minWidth: getWidthFromHeader(headerName || ''),
        };
      });

    // 3️⃣ Add rate columns dynamically
    rateKeys.forEach((rate) => {
      const header = this.formatText(rate) || '';
      columnDefs.push({
        field: rate,
        headerName: header,
        filter: false,
        sortable: false,
        minWidth: getWidthFromHeader(header),
      });
    });

    // 4️⃣ Add rate_bracket columns dynamically
    bracketKeys.forEach((bracketStr) => {
      console.log(bracketStr);

      const { field, header } = JSON.parse(bracketStr);

      columnDefs.push({
        field,
        headerName: header,
        filter: false,
        sortable: false,
        minWidth: getWidthFromHeader(header),
      });
    });

    // 5️⃣ Add Action column at the start
    columnDefs.unshift({
      field: 'action',
      headerName: 'Action',
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      flex: 0,
      filter: false,
      floatingFilter: false,
      sortable: false,
      pinned: 'left',
      lockPosition: 'left',
      lockPinned: true,
      lockVisible: true,
      cellRenderer: ActionButtonsComponent,
      suppressHeaderMenuButton: true,
      cellRendererParams: { onStatusUpdate: () => this.triggerGridRefresh() },
    });

    console.log('✅ Generated columnDefs:', columnDefs);
    return columnDefs;
  };

  transformData = (rawData: any[]): any[] => {
    if (!Array.isArray(rawData)) return [];

    const transformed = rawData.map((item) => {
      const {
        group,
        origin,
        destination,
        status,
        rate_category,
        applied_terms,
        rates,
        rate_brackets,
        ...rest
      } = item;

      let displayStatus = this.formatText(status);
      if (status === 'published' || status === 'active') displayStatus = 'Live';
      if (status === 'inactive' || status === 'deactivated') displayStatus = 'Deactivated';

      const transformedItem: any = {
        ...rest,
        group: group?.name ?? null,
        origin_name: origin?.name ?? null,
        destination_name: destination?.name ?? null,
        status: displayStatus,
        rate_category: this.formatText(rate_category?.name),
        applied_terms: this.formatText(applied_terms?.name),
      };

      // --- Flatten rates object into columns ---
      if (rates && typeof rates === 'object') {
        for (const key in rates) {
          if (rates[key]?.value != null) {
            transformedItem[key] = rates[key].value;
          }
        }
      }

      // --- Flatten rate_brackets array into columns ---
      if (Array.isArray(rate_brackets)) {
        rate_brackets.forEach((bracket) => {
          const colName = this.formatBracketFieldName(
            bracket.range_from,
            bracket.range_to,
            bracket.unit_code
          );
          transformedItem[colName] = Number(bracket.bracket_rate);
        });
      }

      return transformedItem;
    });

    console.log('Transformed columns with rates and brackets:', transformed);

    return transformed;
  };

  formatText = (key: string): string => {
    if (!key) return '';
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  formatBracketFieldName = (from: number, to: number, unit_code?: string) => {
    const safeFrom = String(from).replace(/[\.\-]/g, '_');
    const safeTo = String(to).replace(/[\.\-]/g, '_');
    const safeUnit = (unit_code || 'KG').replace(/[\.\-\s]/g, '_');
    return `bracket_${safeFrom}_${safeTo}_${safeUnit}`;
  };

  formatBracketHeaderName = (from: number, to: number, unit_code?: string) => {
    return `${from} - ${to} ${unit_code || 'KG'}`;
  };
}
